import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Shield, Check, X, Edit, Trash2, Plus, Search } from 'lucide-react';

const AthletesTab = ({ 
    athletes,
    filteredAthletes,
    filter,
    setFilter,
    sports,
    uniqueSchools,
    selectedAthletes,
    onSelectAll,
    onSelectAthlete,
    onVerify,
    onReject,
    onDelete,
    onEdit,
    onCreate,
    onBulkVerify,
    onBulkReject,
    onBulkDelete,
    onClearSelection
}) => {
    return (
        <div className="space-y-6">
            <div className="flex flex-wrap gap-4 p-4 bg-[#0d2847] border border-white/5 items-center">
                <div className="relative flex-1 min-w-[220px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Search by name or country..."
                        value={filter.search || ''}
                        onChange={(e) => setFilter({...filter, search: e.target.value})}
                        className="pl-9 bg-[#0d2847] border-white/10"
                        data-testid="athletes-search-input"
                    />
                </div>
                <Select value={filter.verified} onValueChange={(v) => setFilter({...filter, verified: v})}>
                    <SelectTrigger className="w-40 bg-[#0d2847] border-white/10">
                        <SelectValue placeholder="Verification" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d2847] border-white/10">
                        <SelectItem value="all">All Athletes</SelectItem>
                        <SelectItem value="true">Verified</SelectItem>
                        <SelectItem value="false">Pending</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={filter.sport} onValueChange={(v) => setFilter({...filter, sport: v})}>
                    <SelectTrigger className="w-48 bg-[#0d2847] border-white/10">
                        <SelectValue placeholder="Sport" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d2847] border-white/10">
                        <SelectItem value="all">All Sports</SelectItem>
                        {sports.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={filter.school} onValueChange={(v) => setFilter({...filter, school: v})}>
                    <SelectTrigger className="w-48 bg-[#0d2847] border-white/10">
                        <SelectValue placeholder="School" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d2847] border-white/10 max-h-60">
                        <SelectItem value="all">All Schools</SelectItem>
                        {uniqueSchools.map(school => <SelectItem key={school} value={school}>{school}</SelectItem>)}
                    </SelectContent>
                </Select>
                
                {/* Bulk Actions for Athletes */}
                {selectedAthletes.length > 0 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/30">
                        <span className="text-sm text-primary font-medium">
                            {selectedAthletes.length} selected
                        </span>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={onBulkVerify}
                            className="text-success hover:text-success hover:bg-success/20 h-7 text-xs"
                            data-testid="bulk-verify-athletes-btn"
                        >
                            <Check className="w-3 h-3 mr-1" />
                            Verify
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={onBulkReject}
                            className="text-warning hover:text-warning hover:bg-warning/20 h-7 text-xs"
                            data-testid="bulk-reject-athletes-btn"
                        >
                            <X className="w-3 h-3 mr-1" />
                            Reject
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={onBulkDelete}
                            className="text-destructive hover:text-destructive hover:bg-destructive/20 h-7 text-xs"
                            data-testid="bulk-delete-athletes-btn"
                        >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={onClearSelection}
                            className="text-muted-foreground hover:text-white h-7 text-xs"
                        >
                            <X className="w-3 h-3" />
                        </Button>
                    </div>
                )}
                
                <span className="text-sm text-muted-foreground flex-1 text-right">
                    Showing {filteredAthletes.length} of {athletes.length} athletes
                </span>
                <Button
                    onClick={onCreate}
                    className="bg-primary hover:bg-cyan-400 text-[#0a1628]"
                    data-testid="create-athlete-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Athlete
                </Button>
            </div>

            <div className="bg-[#0d2847] border border-white/5 overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-[#0d2847]">
                        <tr>
                            <th className="text-left p-4 w-12">
                                <input
                                    type="checkbox"
                                    checked={filteredAthletes.length > 0 && filteredAthletes.every(a => selectedAthletes.includes(a.id))}
                                    onChange={(e) => onSelectAll(e.target.checked)}
                                    className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                                    data-testid="select-all-athletes-checkbox"
                                />
                            </th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Name</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Country</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">School</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Sports</th>
                            <th className="text-left text-xs tracking-widest uppercase text-muted-foreground p-4">Status</th>
                            <th className="text-right text-xs tracking-widest uppercase text-muted-foreground p-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredAthletes.map((athlete) => (
                            <tr key={athlete.id} className={`border-t border-white/5 hover:bg-white/5 ${selectedAthletes.includes(athlete.id) ? 'bg-primary/5' : ''}`}>
                                <td className="p-4">
                                    <input
                                        type="checkbox"
                                        checked={selectedAthletes.includes(athlete.id)}
                                        onChange={(e) => onSelectAthlete(athlete.id, e.target.checked)}
                                        className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                                        data-testid={`select-athlete-${athlete.id}`}
                                    />
                                </td>
                                <td className="p-4">
                                    <div>
                                        <p className="font-medium">{athlete.full_name}</p>
                                        <p className="text-xs text-muted-foreground">{athlete.age_category}</p>
                                    </div>
                                </td>
                                <td className="p-4 text-sm text-muted-foreground">{athlete.country}</td>
                                <td className="p-4 text-sm text-muted-foreground">{athlete.school || '-'}</td>
                                <td className="p-4">
                                    <div className="flex flex-wrap gap-1">
                                        {athlete.sports?.slice(0, 2).map(s => (
                                            <span key={s} className="px-2 py-0.5 bg-white/5 text-xs">{s}</span>
                                        ))}
                                        {athlete.sports?.length > 2 && <span className="text-xs text-muted-foreground">+{athlete.sports.length - 2}</span>}
                                    </div>
                                </td>
                                <td className="p-4">
                                    {athlete.verified ? (
                                        <span className="flex items-center gap-1 text-success text-sm">
                                            <Shield className="w-3 h-3" /> Verified
                                        </span>
                                    ) : (
                                        <span className="text-warning text-sm">Pending</span>
                                    )}
                                </td>
                                <td className="p-4">
                                    <div className="flex items-center justify-end gap-1">
                                        <Button size="sm" variant="ghost" onClick={() => onEdit(athlete)} className="text-muted-foreground hover:text-white">
                                            <Edit className="w-4 h-4" />
                                        </Button>
                                        {!athlete.verified && (
                                            <Button size="sm" variant="ghost" onClick={() => onVerify(athlete.id)} className="text-success hover:text-success">
                                                <Check className="w-4 h-4" />
                                            </Button>
                                        )}
                                        {athlete.verified && (
                                            <Button size="sm" variant="ghost" onClick={() => onReject(athlete.id)} className="text-warning hover:text-warning">
                                                <X className="w-4 h-4" />
                                            </Button>
                                        )}
                                        <Button size="sm" variant="ghost" onClick={() => onDelete(athlete.id)} className="text-destructive hover:text-destructive">
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredAthletes.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">No athletes found</div>
                )}
            </div>
        </div>
    );
};

export default AthletesTab;
